import React, { FC } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { DialogTextActionButton } from "../Base/DialogTextActionButton";

export type BalancePaymentLinkPreview = {
  playerLabel: string;
  paidBoardNames: string[];
  unpaidBoardNames: string[];
  totalEuros: number;
  paidEuros: number;
  dueEuros: number;
};

type BalancePaymentLinkDialogProps = {
  open: boolean;
  preview: BalancePaymentLinkPreview | null;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const BalancePaymentLinkDialog: FC<BalancePaymentLinkDialogProps> = ({
  open,
  preview,
  loading,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Lien de paiement Stripe</DialogTitle>
      <DialogContent>
        {preview && (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body1">
              Joueur : <strong>{preview.playerLabel}</strong>
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Le montant du lien est le reste à payer sur{" "}
              <strong>l&apos;ensemble des tableaux</strong> du joueur (grille
              tarifaire par paliers), pas le prix d&apos;une seule ligne.
            </Typography>

            {preview.paidBoardNames.length > 0 && (
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">
                  Déjà pris en compte ({preview.paidEuros} €)
                </Typography>
                <List dense disablePadding>
                  {preview.paidBoardNames.map((name) => (
                    <ListItem key={`paid-${name}`} disableGutters sx={{ py: 0 }}>
                      <ListItemText primary={name} />
                    </ListItem>
                  ))}
                </List>
              </Stack>
            )}

            <Stack spacing={0.5}>
              <Typography variant="subtitle2">
                Tableaux non soldés
                {preview.unpaidBoardNames.length === 0 ? " : aucun" : ""}
              </Typography>
              {preview.unpaidBoardNames.length > 0 && (
                <List dense disablePadding>
                  {preview.unpaidBoardNames.map((name) => (
                    <ListItem
                      key={`unpaid-${name}`}
                      disableGutters
                      sx={{ py: 0 }}
                    >
                      <ListItemText primary={name} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Stack>

            <Typography variant="body2">
              Tarif total inscription : {preview.totalEuros} €
              {preview.paidEuros > 0
                ? ` − déjà payé ${preview.paidEuros} €`
                : ""}
            </Typography>

            <Typography
              variant="h6"
              fontWeight={700}
              sx={(t) => ({
                color:
                  t.palette.mode === "dark"
                    ? t.palette.secondary.light
                    : t.palette.secondary.dark,
              })}
            >
              Montant du lien : {preview.dueEuros} €
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <DialogTextActionButton onClick={onClose} disabled={loading}>
          Annuler
        </DialogTextActionButton>
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirm}
          disabled={loading || !preview || preview.dueEuros <= 0}
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : undefined
          }
        >
          {loading ? "Génération…" : "Générer et copier le lien"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BalancePaymentLinkDialog;
